import { ky } from './ky';
import { ListResponse } from './common';
import { ProductDto, ProductStatus } from './product';
import { OrderLineDto, OrderStatus, ShipmentStatus, ShippingAddressDto, ShippingAddressRequest } from './order';

export type { ShippingAddressDto, ShippingAddressRequest };

/**
 * 브랜드 판매자 주문 DTO. 백엔드 `OrderDto`(commerce/data/OrderDto.kt)와 매칭된다.
 * 구매자용 `OrderStatusDto`와 달리 결제/배송/반품/교환 요약을 포함하지 않고 주문 상태만 노출한다.
 */
export interface BrandOrderDto {
  id: number;
  orderNumber: string;
  buyerMemberId: number;
  sellerAccountId: number;
  status: OrderStatus;
  totalAmount: number;
  currencyCode: string;
  createdAt?: string | null;
  lines: OrderLineDto[];
  shippingAddress?: ShippingAddressDto | null;
}

/**
 * 브랜드 상품 상태 변경 요청. 백엔드 `BrandProductStatusUpdateRequest`와 매칭된다.
 * DRAFT는 백엔드에서 400으로 거부되므로 ON_SALE/SUSPENDED만 사용한다.
 */
export interface BrandProductStatusUpdateRequest {
  status: Extract<ProductStatus, 'ON_SALE' | 'SUSPENDED'>;
}

/**
 * 배송 생성 요청. 백엔드 `ShipmentCreateRequest`와 매칭된다.
 * `address`를 생략하면 백엔드가 주문의 배송지 스냅샷을 기본값으로 사용한다.
 */
export interface ShipmentCreateRequest {
  address?: ShippingAddressRequest | null;
  shippingFeeAmount?: number;
  remoteAreaAdditionalFeeAmount?: number;
  freeShippingApplied?: boolean;
  currencyCode?: string;
  returnWindowDays?: number;
  exchangeWindowDays?: number;
}

/** 배송 DTO. 백엔드 `ShipmentDto`와 매칭된다. */
export interface ShipmentDto {
  id: number;
  orderId: number;
  buyerMemberId: number;
  sellerAccountId: number;
  status: ShipmentStatus;
  address: ShippingAddressDto;
  shippingFeeAmount: number;
  remoteAreaAdditionalFeeAmount: number;
  freeShippingApplied: boolean;
  currencyCode: string;
  courierName?: string | null;
  trackingNumber?: string | null;
  returnWindowDays: number;
  exchangeWindowDays: number;
  preparedAt: string;
  shippedAt?: string | null;
  inTransitAt?: string | null;
  deliveredAt?: string | null;
  purchaseConfirmedAt?: string | null;
}

/** 정산 상태. 백엔드 `SettlementStatus` enum과 매칭된다. */
export type SettlementStatus = 'PENDING' | 'CONFIRMED' | 'PAID' | 'ADJUSTED';

/** 정산 원장 항목 유형. 백엔드 `SettlementLedgerEntryType` enum과 매칭된다. */
export type SettlementLedgerEntryType =
  | 'GROSS_SALE'
  | 'SHIPPING_FEE'
  | 'PLATFORM_COMMISSION'
  | 'PAYMENT_FEE'
  | 'REWARD_COST'
  | 'REFUND_DEDUCTION'
  | 'ADJUSTMENT_CREDIT'
  | 'ADJUSTMENT_DEBIT';

/** 정산 원장 항목. 백엔드 `SettlementLedgerEntryDto`와 매칭된다. */
export interface SettlementLedgerEntryDto {
  id: number;
  settlementId: number;
  orderId: number;
  sellerAccountId: number;
  orderLineId?: number | null;
  paymentId?: number | null;
  refundId?: number | null;
  rewardLedgerEntryId?: number | null;
  creditedAccountId?: number | null;
  entryType: SettlementLedgerEntryType;
  amount: number;
  currencyCode: string;
  memo?: string | null;
  occurredAt: string;
}

/** 정산 DTO. 백엔드 `SettlementDto`와 매칭된다. */
export interface SettlementDto {
  id: number;
  orderId: number;
  paymentId: number;
  sellerAccountId: number;
  status: SettlementStatus;
  grossSalesAmount: number;
  commissionAmount: number;
  paymentFeeAmount: number;
  shippingFeeAmount: number;
  rewardCostAmount: number;
  refundDeductionAmount: number;
  settlementAmount: number;
  currencyCode: string;
  commissionPolicyId?: number | null;
  commissionRateBasisPoints: number;
  paymentFeeRateBasisPoints: number;
  settlementAvailableAt: string;
  confirmedAt?: string | null;
  paidAt?: string | null;
  adjustedAt?: string | null;
  ledgerEntries: SettlementLedgerEntryDto[];
}

/**
 * 브랜드 판매자 콘솔 API 클라이언트.
 * 백엔드 `BrandCommerceAdminController`(/v1/commerce/brand)와 매칭된다.
 * 모든 엔드포인트는 `sellerAccountId` 쿼리 파라미터를 요구한다.
 */
export default class BrandCommerceService {
  /** 판매자 상품 목록 조회 */
  async listProducts(sellerAccountId: number) {
    const response = await ky.get('v1/commerce/brand/products', {
      searchParams: { sellerAccountId },
    });
    return (await response.json()) as ListResponse<ProductDto>;
  }

  /** 상품 상태 변경 (ON_SALE=판매 시작, SUSPENDED=판매 중지) */
  async updateProductStatus(productId: number, request: BrandProductStatusUpdateRequest) {
    const response = await ky.patch(`v1/commerce/brand/products/${productId}/status`, {
      json: request,
    });
    return (await response.json()) as ProductDto;
  }

  /** 판매자 주문 목록 조회 */
  async listOrders(sellerAccountId: number) {
    const response = await ky.get('v1/commerce/brand/orders', {
      searchParams: { sellerAccountId },
    });
    return (await response.json()) as ListResponse<BrandOrderDto>;
  }

  /** 판매자 주문 상세 조회 */
  async getOrder(orderId: number, sellerAccountId: number) {
    const response = await ky.get(`v1/commerce/brand/orders/${orderId}`, {
      searchParams: { sellerAccountId },
    });
    return (await response.json()) as BrandOrderDto;
  }

  /** 배송 생성 (주문 배송 처리) */
  async createShipment(orderId: number, sellerAccountId: number, request: ShipmentCreateRequest) {
    const response = await ky.post(`v1/commerce/brand/orders/${orderId}/shipments`, {
      searchParams: { sellerAccountId },
      json: request,
    });
    return (await response.json()) as ShipmentDto;
  }

  /** 판매자 정산 목록 조회 */
  async listSettlements(sellerAccountId: number) {
    const response = await ky.get('v1/commerce/brand/settlements', {
      searchParams: { sellerAccountId },
    });
    return (await response.json()) as ListResponse<SettlementDto>;
  }
}
